import { THINGS_TO_DO_HEADING } from './ThingsToDo/thingsToDoMessages'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'

export function useTripPlanDetailDrawerCopy(countryName?: string) {
  const intl = useIntl()

  const formatSeasonWeather = useCallback((minTemp: number, maxTemp: number, weather: string) => {
    return intl.formatMessage(
      {
        id: 'tPJwyH7YEk9ZRyrVnED-',
        defaultMessage: '{minTemp}°C – {maxTemp}°C Avg. {weather}',
        description: 'Temperature range and average weather for a season in best time to visit table',
      },
      { minTemp, maxTemp, weather },
    )
  }, [intl])

  const formatAreaName = useCallback((index: number, area: string) => {
    return intl.formatMessage(
      {
        id: 'o2LDDooOjsw6gA-UYSuf',
        defaultMessage: '{index}. {area}',
        description: 'Numbered area name in top areas list in the trip planner AI chat',
      },
      { index, area },
    )
  }, [intl])

  const formatThingsToDoHeading = useCallback((placeName: string) => {
    return intl.formatMessage(THINGS_TO_DO_HEADING, { placeName })
  }, [intl])

  return useMemo(() => ({
    formatThingsToDoHeading,
    // "More", not "all": the rows above it are already the top ones.
    viewMoreThingsToDoLabel: intl.formatMessage({
      id: 'LbSxXUYxZripYclIT2Zb',
      defaultMessage: 'View more',
      description: 'Link below the previewed places list in the trip planner destination drawer that opens the full things to do search drawer',
    }),
    failedToLoadText: intl.formatMessage({
      id: 'FA17vx7NLkSZhFIBlGQD',
      defaultMessage: 'Failed to load additional information.',
      description: 'Error text when all trip plan detail sections fail to load',
    }),
    retryLabel: intl.formatMessage({
      id: 'U-ZcfakqpLsj2dlV-u4M',
      defaultMessage: 'Retry',
      description: 'Button label to retry loading trip plan detail sections',
    }),
    cityInCountryText: intl.formatMessage(
      {
        id: 'IrCYQGrjDZfcICrhlbRR',
        defaultMessage: 'City in {countryName}',
        description: 'Subtitle showing which country a city belongs to in trip plan detail',
      },
      { countryName: countryName ? formatPlacePhrase({ location: { name: countryName }, language: intl.locale, intent: 'location' }) : undefined },
    ),
    viewGalleryLabel: intl.formatMessage({
      id: 'VR9gBhLOmH6uKdz4K4GZ',
      defaultMessage: 'View gallery',
      description: 'Button label to open image gallery lightbox in trip plan detail',
    }),
    bestTimeToVisitHeading: intl.formatMessage({
      id: 'S798osdLcKcFQLKzPVwr',
      defaultMessage: 'Best Time to Visit',
      description: 'Heading for best time to visit section in trip plan detail',
    }),
    seasonHeader: intl.formatMessage({
      id: 'oZpAAoObVMOQuOdqY-Wl',
      defaultMessage: 'Season',
      description: 'Table header for season column in best time to visit section',
    }),
    weatherHeader: intl.formatMessage({
      id: 'JBSlAQSIQQaoKtdei1de',
      defaultMessage: 'Weather',
      description: 'Table header for weather column in best time to visit section',
    }),
    whyVisitHeader: intl.formatMessage({
      id: 'xnx6SqiQiV6CBZpeEg1R',
      defaultMessage: 'Why Visit',
      description: 'Table header for why visit column in best time to visit section',
    }),
    whatYoullLoveHeading: intl.formatMessage({
      id: 'neot_xLo154w2TBir6Y4',
      defaultMessage: 'What you\u2019ll love',
      description: 'Heading for what to love section in trip plan detail',
    }),
    topAreasHeading: intl.formatMessage({
      id: '5qveZGlLczJjuvPraVc7',
      defaultMessage: 'Top areas to visit',
      description: 'Heading for top areas to visit section in trip plan detail',
    }),
    insiderTipsHeading: intl.formatMessage({
      id: 'SmqFMCdvG9iJmtM4tF3s',
      defaultMessage: 'Insider Tips',
      description: 'Heading for insider tips section in trip plan detail',
    }),
    locationHeading: intl.formatMessage({
      id: 'YrfaTmB2ca_D95RIXBwK',
      defaultMessage: 'Location',
      description: 'Heading for location map section in trip plan detail',
    }),
    formatSeasonWeather,
    formatAreaName,
  }), [intl, countryName, formatSeasonWeather, formatAreaName, formatThingsToDoHeading])
}
